import { ruleImpl } from './rule-impl';
/**
 * How far back an answer is pinned: 2 for an escape (`exclusive`), 1 for a
 * catch-all (`keepLast`), 0 for neither. An escape ranks as an escape whether
 * or not it also says `keepLast`: every reader (this order, the report's row
 * roles, the twins' shuffle) reads the two flags this way, so `keepLast` on an
 * escape pins nothing further.
 */
export function pinRank(option: unknown): 0 | 1 | 2 {
  const flags = option as { exclusive?: unknown; keepLast?: unknown } | null;
  if (flags?.exclusive === true) return 2;
  return flags?.keepLast === true ? 1 : 0;
}

/**
 * Where a select's pinned answers sit (D0/D12): after the other answers, the
 * catch-alls (`keepLast`, "Other") and then the escapes (`exclusive`, "None of
 * these"), each group in its written order. It is the order shown with
 * randomization off and the tail a shuffle keeps, so a mark never leaves a
 * pinned answer mid-list (E-questions-09). The one order, read by the
 * editor's response shape, the operations applier and the one normalize every
 * write goes through (`normalizeResponse`, N1), so a Build commit is ordered
 * by it too: stamping (Python) keeps AUTHOR's order, and this is the one
 * owner of where pinned answers sit (`derived_exclusive_options_last`). Single
 * and multi selects only: a ranking carries neither flag (D0a).
 *
 * Returns the list itself when it is already in that order.
 */
export function pinnedLastOrder<T>(options: ReadonlyArray<T>): ReadonlyArray<T> {
  const ordered = options
    .map((option, index) => ({ option, index, rank: pinRank(option) }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map(({ option }) => option);
  return ordered.every((option, index) => option === options[index])
    ? options
    : ordered;
}

/**
 * A select's answers in the order its writer's list is kept: the user's own
 * list (`givenOrder`, stamped from the literal answers they wrote) stays whole,
 * an escape they placed first or in the middle included (owner 2026-10-23);
 * every other list has its pinned answers last (`pinnedLastOrder`).
 */
export function selectOptionsOrder<T>(
  options: ReadonlyArray<T>,
  givenOrder?: unknown,
): ReadonlyArray<T> {
  return givenOrder === true ? options : pinnedLastOrder(options);
}

/**
 * Whether an answer may be pinned, as the opt-out (`exclusive`) or a
 * catch-all (`keepLast`): an answer the question owns. One that stands for a
 * tested asset (a typed `choice` ref) or for one of the study's brands
 * (`brandRole`, the focal brand or a competitor) is a real answer, never
 * "none of these" or "other" (E-questions-15). The editor offers the marks by
 * it, and the operations applier refuses a write that pins such an answer
 * (`pinnedRealAnswerRefusals`).
 */
export function isPinnableAnswer(option: unknown): boolean {
  if (typeof option !== "object" || option === null) return false;
  const { assetRefs, brandRole } = option as {
    assetRefs?: unknown;
    brandRole?: unknown;
  };
  const standsForAsset =
    Array.isArray(assetRefs) &&
    assetRefs.some(
      (ref) =>
        typeof ref === "object" &&
        ref !== null &&
        (ref as { role?: unknown }).role === "choice",
    );
  return !standsForAsset && brandRole === undefined;
}

export interface PinnedRealAnswerRefusal {
  code: "real_answer_pinned";
  /** The list (`options` or `categories`) and the answer's index in it. */
  list: "options" | "categories";
  index: number;
  flag: "exclusive" | "keepLast";
}

/**
 * The answers a write pins although they are real answers
 * (`isPinnableAnswer`): flagged in `after` and not so flagged on the same
 * answer (by id) in `before`. An answer flagged already keeps its flag, so
 * the write can take it off.
 */
export function pinnedRealAnswerRefusals(
  before: unknown,
  after: unknown,
): Array<PinnedRealAnswerRefusal> {
  const listOf = (response: unknown, list: "options" | "categories") => {
    const items = (response as Record<string, unknown> | null)?.[list];
    return Array.isArray(items)
      ? items.filter(
          (item): item is Record<string, unknown> =>
            typeof item === "object" && item !== null,
        )
      : [];
  };
  const refusals: Array<PinnedRealAnswerRefusal> = [];
  for (const list of ["options", "categories"] as const) {
    const previous = new Map(
      listOf(before, list).map((item) => [String(item.id ?? ""), item]),
    );
    listOf(after, list).forEach((item, index) => {
      if (isPinnableAnswer(item)) return;
      const was = previous.get(String(item.id ?? ""));
      for (const flag of ["exclusive", "keepLast"] as const) {
        if (item[flag] === true && was?.[flag] !== true) {
          refusals.push({ code: "real_answer_pinned", list, index, flag });
        }
      }
    });
  }
  return refusals;
}

// Each rule below is decided by the function named (wave H T3, move 11).
ruleImpl('real_answer_pinned', pinnedRealAnswerRefusals);
